import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CartContext = createContext(null);

export const formatPrice = (product, currency, qty = 1) => {
  if (currency === "IDR") {
    return `Rp ${(product.price_idr * qty).toLocaleString("id-ID")}`;
  }
  return `$${(product.price_usd * qty).toFixed(2)}`;
};

export const CartProvider = ({ children }) => {
  const { i18n } = useTranslation();
  const [products, setProducts] = useState([]);
  const [config, setConfig] = useState({ midtrans_enabled: false, whatsapp_number: "628111042520" });
  const [items, setItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem("agrodyke_cart")) || []; } catch { return []; }
  });
  const [currencyOverride, setCurrencyOverride] = useState(() => localStorage.getItem("agrodyke_currency") || "");
  const [cartOpen, setCartOpen] = useState(false);

  const lang = i18n.language?.split("-")[0] || "id";
  const currency = currencyOverride || (lang === "id" ? "IDR" : "USD");

  useEffect(() => {
    axios.get(`${API}/products`).then((r) => setProducts(r.data)).catch(() => {});
    axios.get(`${API}/config`).then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  useEffect(() => { localStorage.setItem("agrodyke_cart", JSON.stringify(items)); }, [items]);

  const setCurrency = (c) => {
    setCurrencyOverride(c);
    localStorage.setItem("agrodyke_currency", c);
  };

  const addItem = (product_id, qty = 1) => {
    setItems((prev) => {
      const found = prev.find((i) => i.product_id === product_id);
      if (found) return prev.map((i) => (i.product_id === product_id ? { ...i, quantity: i.quantity + qty } : i));
      return [...prev, { product_id, quantity: Math.max(5, qty) }];
    });
  };

  const updateQty = (product_id, qty) => {
    setItems((prev) => (qty <= 0 ? prev.filter((i) => i.product_id !== product_id)
      : prev.map((i) => (i.product_id === product_id ? { ...i, quantity: Math.max(5, qty) } : i))));
  };

  const clearCart = () => setItems([]);

  const totals = useMemo(() => {
    let idr = 0, usd = 0, count = 0;
    items.forEach((i) => {
      const p = products.find((pr) => pr.product_id === i.product_id);
      if (p) { idr += p.price_idr * i.quantity; usd += p.price_usd * i.quantity; count += i.quantity; }
    });
    return { idr, usd, count };
  }, [items, products]);

  const totalLabel = currency === "IDR" ? `Rp ${totals.idr.toLocaleString("id-ID")}` : `$${totals.usd.toFixed(2)}`;

  const value = { products, config, items, addItem, updateQty, clearCart, totals, totalLabel,
    currency, setCurrency, cartOpen, setCartOpen, lang };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => useContext(CartContext);
export { API };
